import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";

import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import BulkListModal from "./BulkListModal";
import PartImage from "../../stock/PartImage";
import PopOutBox from "../../shared/PopOutBox";
import { IPartCategory } from '../../../models/Part';
import { PageMode } from "../../../models/PagedTable";
import UnlistedOfferPrice from "./UnlistedOfferPrice";
import VehiclePagedTable from "../../shared/Tables/VehiclePagedTable";
import UnlistedReadyStatus from "./UnlistedReadyStatus";
import { IPart, IPartImageType } from "../../../models/Part";
import usePartMutations from "../../../data/usePartMutations";
import useNotification from "../../notifications/useNotifications";
import UnlistedExpandedRowContent from "./UnlistedExpandedRowContent";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import ChangeCategoryModal from "../../stock/stockFunctions/ChangeCatgeoryModal";
import WarehouseLocationFromId from "../../warehouseLocations/WarehouseLocationFromId";
import WarehouseLocationSearch from "../../warehouseLocations/WarehouseLocationSearch";
import useTemplateProductDescription from "../../../data/useTemplateProductDescription";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";
import DateRangeFilter from "../../shared/DateRangeFilter";
import EbayListingTitle from "../EbayListingTitle";
import { useForm } from "react-hook-form";
import { FormatType, ListingStatusEnum } from "../../../models/EbayOffer";
import { IWarehouseLocation } from "../../../models/Location";
import { useQueryClient } from "@tanstack/react-query";
import useWarehouseLocations from "../../../data/useWarehouseLocations";
import RecentlyListedErrorCell from "../recentlyListed/RecentlyListedErrorCell";
import TableCheckboxCol from "../../shared/Tables/IndeterminateCheckbox";
import useCategoriesPartial from "../../../data/useCategoriesPartial";
import ContactFromId from "../../contacts/ContactFromId";
import DonorVehicleCell from "../../stock/DonorVehicleCell";
import PartGradeBadge from "../../shared/PartGradeBadge";
import PartNotesIndicator from "../../shared/PartNotesIndicator";
import StatusIcons from "../../stock/StatusIcons";
import StockMpnCell from "../../stock/StockMpnCell";
import TriToggleInput, { type TriState } from "../../shared/Inputs/TriToggleInput";
import TableFilterPanel from "../../shared/Tables/TableFilterPanel";
import VehiclePartSearchFilters, {
  buildVehiclePartSearchBody,
  defaultVehicleDetailFilterEnabled,
  IVehiclePartSearchFields,
  VehicleDetailFilterKey,
  vehiclePartSearchDefaultValues,
} from "../../stock/VehiclePartSearchFilters";
import classNames from "../../../utils/classNames";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";

type LocationSearchMode = "equals" | "beginsWith";

interface IFormFields extends IVehiclePartSearchFields {
  partCategory: IPartCategory[];
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
  statusMissingDeleted: TriState;
  statusQc: TriState;
  statusDismantled: TriState;
  hasLocation: TriState;
  hasImages: TriState;
  hasPrice: TriState;
  hasMpn: TriState;
  hasOeMpn: TriState;
};

interface IUnlistedSavedDefaultFilters {
  partCategory: IPartCategory[];
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationBeginsWith: string;
  startDate: string | null;
  endDate: string | null;
  dateFieldType: string;
  statusMissingDeleted: TriState;
  statusQc: TriState;
  statusDismantled: TriState;
  hasLocation: TriState;
  hasImages: TriState;
  hasPrice: TriState;
  hasMpn: TriState;
  hasOeMpn: TriState;
}

const defaultFormValues: IFormFields = {
  ...vehiclePartSearchDefaultValues,
  partCategory: [],
  listingStatus: ListingStatusEnum["All"],
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created",
  statusMissingDeleted: "any",
  statusQc: "any",
  statusDismantled: "any",
  hasLocation: "any",
  hasImages: "any",
  hasPrice: "any",
  hasMpn: "any",
  hasOeMpn: "any",
};

function buildUnlistedTriStateFlags(state: {
  statusMissingDeleted: TriState | undefined;
  statusQc: TriState | undefined;
  statusDismantled: TriState | undefined;
  hasLocation: TriState | undefined;
  hasImages: TriState | undefined;
  hasPrice: TriState | undefined;
  hasMpn: TriState | undefined;
  hasOeMpn: TriState | undefined;
}): Record<string, true | undefined> {
  const map = (value: TriState | undefined, withKey: string, withoutKey: string): Record<string, true> => {
    if (value === "with") return { [withKey]: true };
    if (value === "without") return { [withoutKey]: true };
    return {};
  };

  return {
    ...map(state.statusMissingDeleted, "partsWithMissingOrDeletedStatus", "partsWithoutMissingOrDeletedStatus"),
    ...map(state.statusQc, "partsWithQcStatus", "partsWithoutQcStatus"),
    ...map(state.statusDismantled, "partsWithDismantledStatus", "partsWithoutDismantledStatus"),
    ...map(state.hasLocation, "partsWithLocation", "partsWithoutLocations"),
    ...map(state.hasImages, "partsWithImages", "partsWithoutImages"),
    ...map(state.hasPrice, "partsWithPrice", "partsWithoutPrice"),
    ...map(state.hasMpn, "partsWithMPN", "partsWithoutMPN"),
    ...map(state.hasOeMpn, "partsWithOeMPN", "partsWithoutOeMPN"),
  };
}

export default function EbayUnlistedTable() {
  const { autoReorderImages } = usePartMutations();
  const { useBulkDefaultPartCategory } = usePartDefaultCategoriesMutations();
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { warehouseLocations } = useWarehouseLocations();
  const { categories: allCategories } = useCategoriesPartial();
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const { register, setValue, getValues, watch, reset } = useForm<IFormFields>({
    defaultValues: defaultFormValues,
  });

  const bulkListLoading = false;
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState<boolean>(false);
  const [bulkListModalOpen, setBulkListModalOpen] = useState<boolean>(false);
  const [selectedPartsForBulkList, setSelectedPartsForBulkList] = useState<IPart[] | undefined>(undefined);
  const tableRef = useRef<Table<IPart> | null>(null);
  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const [vehicleDetailFilterEnabled, setVehicleDetailFilterEnabled] = useState<Record<VehicleDetailFilterKey, boolean>>(defaultVehicleDetailFilterEnabled);

  const watchMake = watch("make");
  const watchModel = watch("model");
  const watchTrim = watch("trim");
  const watchYearFrom = watch("yearFrom");
  const watchYearTo = watch("yearTo");
  const watchBodyStyle = watch("bodyStyle");
  const watchNumberOfDoors = watch("numberOfDoors");
  const watchFuelType = watch("fuelType");
  const watchEngineSize = watch("engineSize");
  const watchEngineCode = watch("engineCode");
  const watchTransmission = watch("transmissionType");
  const watchPartNumber = watch("partNumber");
  const watchTagNumber = watch("tagNumber");
  const watchVrm = watch("vrm");
  const watchPartCategory = watch("partCategory");
  const watchListingStatus = watch("listingStatus");
  const watchWarehouseLocation = watch("warehouseLocation");
  const watchLocationSearchMode = watch("locationSearchMode");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");
  const watchStatusMissingDeleted = watch("statusMissingDeleted");
  const watchStatusQc = watch("statusQc");
  const watchStatusDismantled = watch("statusDismantled");
  const watchHasLocation = watch("hasLocation");
  const watchHasImages = watch("hasImages");
  const watchHasPrice = watch("hasPrice");
  const watchHasMpn = watch("hasMpn");
  const watchHasOeMpn = watch("hasOeMpn");

  // const ebayListingOptions = Object.keys(ListingStatusEnum)
  //   .filter(key => isNaN(Number(key)))
  //   .map((key, index) => ({
  //     id: index,
  //     name: key,
  //   }));

  const colHelper = createColumnHelper<IPart>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true, true, "ebay-listings", true),
      colHelper.display({
        id: "images",
        cell: (info) => <PartImage part={info.row.original} width="min-w-10 max-w-32" />
      }),
      colHelper.accessor((row) => row.category?.name, {
        header: "Part Information",
        id: "categoryId",
        sortingFn: "auto",
        sortDescFirst: false,
        meta: { thClassName: "w-64 min-w-[16rem] max-w-[24rem]", tdClassName: "w-64 min-w-[16rem] max-w-[24rem] align-top" },
        cell: (info) => {
          const part = info.row.original;
          const fmt = (iso: string | null | undefined) => iso ? DateTime.fromISO(iso).toFormat("dd LLL yyyy") : null;
          const events = [
            { key: "dismantled", label: "Dismantled", date: fmt(part.removedOn), by: part.removedBy },
            { key: "imaged", label: "Imaged", date: fmt(part.imagedOn), by: part.imagedBy },
            { key: "qc", label: "QC", date: fmt(part.passedQcOn), by: part.passedQcBy },
          ].filter((event) => event.date || event.by);

          return (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{part.category?.name ?? "-"}</span>
                  <PartGradeBadge part={part} />
                  <PartNotesIndicator part={part} />
                </div>
                {part.tagNumber && <div className="text-xs text-slate-500">{part.tagNumber}</div>}
                {events.map((event) => (
                  <div key={event.key} className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                    <span>{event.label}{event.date ? ` ${event.date}` : ""}</span>
                    {event.by && <span className="text-slate-400">by <ContactFromId contactId={event.by} /></span>}
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-2 text-xs text-slate-500">
                <div className="mb-1 font-medium text-slate-600">Location</div>
                <WarehouseLocationFromId id={part.warehouseLocationId} locatedById={part.locatedBy} lastStockChecked={part.lastStockChecked} />
              </div>
            </div>
          );
        },
      }),
      colHelper.accessor("vehicle.combinedMake", {
        header: "Donor Vehicle",
        enableSorting: false,
        meta: { thClassName: "max-w-[18rem] 2xl:w-96 2xl:min-w-[24rem] 2xl:max-w-[28rem]", tdClassName: "max-w-[18rem] align-top 2xl:w-96 2xl:min-w-[24rem] 2xl:max-w-[28rem]" },
        cell: (info) => <DonorVehicleCell part={info.row.original} twoColumn />,
      }),
      colHelper.accessor("partNumber", {
        header: "MPN",
        sortingFn: "auto",
        meta: { thClassName: "min-w-60 max-w-[20rem]", tdClassName: "min-w-60 max-w-[20rem] align-top" },
        cell: (info) => <StockMpnCell part={info.row.original} />,
      }),
      colHelper.accessor(row => {
        const offer = row.offers?.[0];
        const offerPrice = offer
          ? (offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice)
          : null;
        return offerPrice ?? row.enteredPrice ?? 0;
      }, {
        id: "offer.price",
        header: "Price",
        cell: (info) => <UnlistedOfferPrice part={info.row.original} buttonLayout="column" hideLabel />,
      }),
      // colHelper.accessor("offers", {
      //   id: "error state",
      //   header: "Error",
      //   sortingFn: "auto",
      //   cell: (info) => <TableListingError part={info.row.original} />
      // }),
      colHelper.accessor(row => row.images.some(img => img.imageType === IPartImageType.Damage), {
        header: "Damage",
        cell: (info) => {
          const hasDamage = info.getValue();
          const damageImages = info.row.original.images.filter(img => img.imageType === IPartImageType.Damage);

          return (
            <span className="flex justify-center text-xl">
              {hasDamage ? (
                <PopOutBox
                  classes="max-w-3xl w-[40vw] max-h-[50vh] overflow-y-auto"
                  icon={<ExclamationTriangleIcon className="h-6 w-6 text-red-500" />}
                >
                  <div className="p-2">
                    <p className="text-sm font-medium mb-2">Damage Images:</p>
                    <div className="space-y-2">
                      {damageImages.map((image, index) => (
                        <img
                          key={index}
                          src={image.url}
                          alt={`Damage ${index + 1}`}
                          className="w-full h-auto rounded border"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      ))}
                    </div>
                  </div>
                </PopOutBox>
              ) : (
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              )}
            </span>
          );
        },
      }),
      colHelper.display({
        id: "status",
        header: "Status",
        enableSorting: false,
        meta: { thClassName: "min-w-[7rem] max-w-[10rem]", tdClassName: "min-w-[7rem] max-w-[10rem] align-top" },
        cell: (info) => <StatusIcons part={info.row.original} />,
      }),
      colHelper.display({
        header: "Ready To List",
        enableSorting: false,
        cell: (info) => <div className="flex flex-col items-center gap-y-2">
          {info.row.original.batchItem && (
            <RecentlyListedErrorCell batchItem={info.row.original.batchItem} showExtraInfo={false} />
          )}
          <UnlistedReadyStatus part={info.row.original} />
        </div>,
      }),
    ],
    []
  );



  const bulkList = (rows: RowModel<IPart>, table: Table<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    tableRef.current = table;
    setSelectedPartsForBulkList(rows.rows.map((row) => row.original));
    setBulkListModalOpen(true);
  };

  const handleBulkListComplete = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
      tableRef.current = null;
    }
  };

  const handleAutoReorderImages = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const ids = selected.map((row) => row.original.id);

    autoReorderImages.mutate(ids, bulkSaveHandlers);
  }

  const handleApplyBulkDefaults = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const body = {
      partIds: selected.map((row) => row.original.id)
    };

    useBulkDefaultPartCategory.mutate(body as any, {
      ...bulkSaveHandlers,
      onSuccess: () => {
        bulkSaveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);
      }
    });
  };


  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    bulkUpdateProductDescription.mutate({ partIds }, bulkSaveHandlers);
  };


  const handleOpenChangeCategory = (rows: RowModel<IPart>) => {
    setToUpdate(rows);
    setUpdateCategoryOpen(true);
  };

  const expandedRow = (part: IPart) => {
    return <UnlistedExpandedRowContent part={part} />;
  };

  const extraRow = (part: IPart) => {
    return <div className="p-1 py-2">
      <EbayListingTitle part={part} onlyTitle={true} />
    </div>;
  };

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  const handleClearForm = () => {
    reset();
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    setAdditionalBody({});
    // setQueryInput("");
    // setAppliedQuery("");
  };

  useEffect(() => {
    const triFlags = buildUnlistedTriStateFlags({
      statusMissingDeleted: watchStatusMissingDeleted,
      statusQc: watchStatusQc,
      statusDismantled: watchStatusDismantled,
      hasLocation: watchHasLocation,
      hasImages: watchHasImages,
      hasPrice: watchHasPrice,
      hasMpn: watchHasMpn,
      hasOeMpn: watchHasOeMpn,
    });

    const vehiclePartSearchBody = isSmartMatch
      ? {
        smartSearch: watchVrm,
        categoryIds: watchPartCategory?.map((category) => category.id),
      }
      : buildVehiclePartSearchBody(getValues(), vehicleDetailFilterEnabled);

    setAdditionalBody({
      ...vehiclePartSearchBody,
      listingStatus: watchListingStatus,
      locationSearchMode: watchLocationSearchMode,
      warehouseLocationId: watchLocationSearchMode === "equals" ? watchWarehouseLocation?.id : undefined,
      warehouseLocationBeginsWith: watchLocationSearchMode === "beginsWith" ? watchWarehouseLocationBeginsWith || undefined : undefined,
      startDate: dates.startDate?.toISOString(),
      endDate: dates.endDate?.toISOString(),
      dateFieldType: dateFieldType,
      ...triFlags,
    });

  }, [getValues, isSmartMatch, vehicleDetailFilterEnabled, watchMake, watchModel, watchTrim, watchYearFrom, watchYearTo, watchBodyStyle, watchNumberOfDoors, watchFuelType, watchEngineSize, watchEngineCode, watchTransmission, watchPartNumber, watchTagNumber, watchVrm, watchPartCategory, watchListingStatus, watchWarehouseLocation, watchLocationSearchMode, watchWarehouseLocationBeginsWith, dates, dateFieldType, watchStatusMissingDeleted, watchStatusQc, watchStatusDismantled, watchHasLocation, watchHasImages, watchHasPrice, watchHasMpn, watchHasOeMpn]);

  // Monitor PagedTable query state to prevent crashes
  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter((query) => (query.queryKey[0] === "pagedTable" || query.queryKey[0] === "vehiclePagedTable"));
      setQueryState(pagedTableQueries[pagedTableQueries.length - 1]?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching,
    [queryState, isSearching]
  );

  const activeFilterPills = useMemo(() => {
    const pills: { key: string; label: string; onClear: () => void }[] = [];
    const triLabel = (label: string, value: TriState | undefined) => value && value !== "any" ? `${label}: ${value === "with" ? "With" : "Without"}` : undefined;

    if (watchPartCategory?.length) {
      pills.push({
        key: "partCategory",
        label: `Categories: ${watchPartCategory.length}`,
        onClear: () => setValue("partCategory", [] as any),
      });
    }

    if (watchWarehouseLocation?.id || watchWarehouseLocationBeginsWith) {
      pills.push({
        key: "warehouseLocation",
        label: `Location: ${watchWarehouseLocation?.displayName ?? watchWarehouseLocationBeginsWith}`,
        onClear: () => {
          setValue("warehouseLocation", undefined as any);
          setValue("warehouseLocationId", undefined as any);
          setValue("warehouseLocationBeginsWith", "" as any);
        },
      });
    }

    if (dates.startDate || dates.endDate) {
      pills.push({
        key: "dates",
        label: `Dates: ${dateFieldType}`,
        onClear: () => setDates({ startDate: null, endDate: null }),
      });
    }

    [
      { key: "statusMissingDeleted", label: triLabel("Missing/Deleted", watchStatusMissingDeleted), clear: () => setValue("statusMissingDeleted", "any" as any) },
      { key: "statusQc", label: triLabel("QC", watchStatusQc), clear: () => setValue("statusQc", "any" as any) },
      { key: "statusDismantled", label: triLabel("Dismantled", watchStatusDismantled), clear: () => setValue("statusDismantled", "any" as any) },
      { key: "hasLocation", label: triLabel("Location", watchHasLocation), clear: () => setValue("hasLocation", "any" as any) },
      { key: "hasImages", label: triLabel("Images", watchHasImages), clear: () => setValue("hasImages", "any" as any) },
      { key: "hasPrice", label: triLabel("Price", watchHasPrice), clear: () => setValue("hasPrice", "any" as any) },
      { key: "hasMpn", label: triLabel("MPN", watchHasMpn), clear: () => setValue("hasMpn", "any" as any) },
      { key: "hasOeMpn", label: triLabel("OE MPN", watchHasOeMpn), clear: () => setValue("hasOeMpn", "any" as any) },
    ].forEach((pill) => {
      if (!pill.label) return;
      pills.push({ key: pill.key, label: pill.label, onClear: pill.clear });
    });

    return pills;
  }, [watchPartCategory, watchWarehouseLocation?.id, watchWarehouseLocation?.displayName, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, watchStatusMissingDeleted, watchStatusQc, watchStatusDismantled, watchHasLocation, watchHasImages, watchHasPrice, watchHasMpn, watchHasOeMpn, setValue]);

  const getDefaultFilters = useCallback((): IUnlistedSavedDefaultFilters => ({
    partCategory: watchPartCategory,
    listingStatus: watchListingStatus,
    locationSearchMode: watchLocationSearchMode,
    warehouseLocation: watchWarehouseLocation,
    warehouseLocationBeginsWith: watchWarehouseLocationBeginsWith,
    startDate: dates.startDate?.toISOString() ?? null,
    endDate: dates.endDate?.toISOString() ?? null,
    dateFieldType,
    statusMissingDeleted: watchStatusMissingDeleted,
    statusQc: watchStatusQc,
    statusDismantled: watchStatusDismantled,
    hasLocation: watchHasLocation,
    hasImages: watchHasImages,
    hasPrice: watchHasPrice,
    hasMpn: watchHasMpn,
    hasOeMpn: watchHasOeMpn,
  }), [watchPartCategory, watchListingStatus, watchLocationSearchMode, watchWarehouseLocation, watchWarehouseLocationBeginsWith, dates.startDate, dates.endDate, dateFieldType, watchStatusMissingDeleted, watchStatusQc, watchStatusDismantled, watchHasLocation, watchHasImages, watchHasPrice, watchHasMpn, watchHasOeMpn]);

  const applyDefaultFilters = useCallback((savedFilters: IUnlistedSavedDefaultFilters) => {
    setValue("partCategory", savedFilters.partCategory as any);
    setValue("listingStatus", savedFilters.listingStatus as any);
    setValue("locationSearchMode", (savedFilters.locationSearchMode ?? "equals") as any);
    setValue("warehouseLocation", savedFilters.warehouseLocation as any);
    setValue("warehouseLocationId", savedFilters.warehouseLocation?.id as any);
    setValue("warehouseLocationBeginsWith", (savedFilters.warehouseLocationBeginsWith ?? "") as any);
    setValue("statusMissingDeleted", (savedFilters.statusMissingDeleted ?? "any") as any);
    setValue("statusQc", (savedFilters.statusQc ?? "any") as any);
    setValue("statusDismantled", (savedFilters.statusDismantled ?? "any") as any);
    setValue("hasLocation", (savedFilters.hasLocation ?? "any") as any);
    setValue("hasImages", (savedFilters.hasImages ?? "any") as any);
    setValue("hasPrice", (savedFilters.hasPrice ?? "any") as any);
    setValue("hasMpn", (savedFilters.hasMpn ?? "any") as any);
    setValue("hasOeMpn", (savedFilters.hasOeMpn ?? "any") as any);

    setDates({
      startDate: savedFilters.startDate ? new Date(savedFilters.startDate) : null,
      endDate: savedFilters.endDate ? new Date(savedFilters.endDate) : null,
    });
    setDateFieldType(savedFilters.dateFieldType ?? "Created");
  }, [setValue]);


  return (
    <>
      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      {selectedPartsForBulkList && (
        <BulkListModal
          open={bulkListModalOpen}
          setOpen={setBulkListModalOpen}
          selectedParts={selectedPartsForBulkList}
          onComplete={handleBulkListComplete}
        />
      )}

      <form className="my-2 mb-4 space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <VehiclePartSearchFilters
          register={register}
          setValue={setValue}
          getValues={getValues}
          watch={watch}
          categories={allCategories.data?.data ?? []}
          categoriesLoading={allCategories.isLoading}
          disabled={isSearchDisabled}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          isSmartMatch={isSmartMatch}
          setIsSmartMatch={setIsSmartMatch}
          vehicleDetailFilterEnabled={vehicleDetailFilterEnabled}
          setVehicleDetailFilterEnabled={setVehicleDetailFilterEnabled}
          onApplySmartSearch={setAdditionalBody}
          getAdditionalSmartSearchBody={(data) => ({ listingStatus: data.listingStatus })}
        />

        <hr className="mt-1" />

        <TableFilterPanel
          activeFilterPills={activeFilterPills}
          disabled={isSearchDisabled}
          defaultFiltersStorageKey="wilma:tableFilters:unlisted"
          getDefaultFilters={getDefaultFilters}
          onApplyDefaultFilters={applyDefaultFilters}
        >
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">

            <TriToggleInput label="Missing/Deleted" value={watchStatusMissingDeleted} onChange={(value) => setValue("statusMissingDeleted", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="QC" value={watchStatusQc} onChange={(value) => setValue("statusQc", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Dismantled" value={watchStatusDismantled} onChange={(value) => setValue("statusDismantled", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Location" value={watchHasLocation} onChange={(value) => setValue("hasLocation", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Images" value={watchHasImages} onChange={(value) => setValue("hasImages", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="Price" value={watchHasPrice} onChange={(value) => setValue("hasPrice", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="MPN" value={watchHasMpn} onChange={(value) => setValue("hasMpn", value as any)} disabled={isSearchDisabled} />
            <TriToggleInput label="OE MPN" value={watchHasOeMpn} onChange={(value) => setValue("hasOeMpn", value as any)} disabled={isSearchDisabled} />

            <WarehouseLocationSearch
              showSearchOptions={true}
              register={register}
              setValue={setValue}
              watch={watch}
              isSearchDisabled={isSearchDisabled}
              warehouseLocations={warehouseLocations}
            />

            <DateRangeFilter
              dates={dates}
              onDatesChange={handleDatesChange}
              dateFieldType={dateFieldType}
              onDateFieldTypeChange={setDateFieldType}
              disabled={isSearchDisabled}
            />
          </div>
        </TableFilterPanel>

        <ButtonInput
          label="Clear"
          classes={classNames(isSearchDisabled ? "" : "!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100")}
          onClick={handleClearForm}
          disabled={isSearchDisabled}
        />
      </form>


      <VehiclePagedTable
        url="/part/listings"
        pageMode={PageMode.Unlisted}
        columns={cols}
        queryLabel="Search Part Details"
        customUrlQuery=""
        useButtonGroup
        actionButtons={[
          { label: "Bulk List", onClick: bulkList, isLoading: bulkListLoading, disabledOnNoRows: true },
          { label: "Auto Reorder Images", onClick: handleAutoReorderImages, isLoading: autoReorderImages.isLoading, disabledOnNoRows: true },
          { label: "Apply Defaults", onClick: handleApplyBulkDefaults, isLoading: useBulkDefaultPartCategory.isLoading, disabledOnNoRows: true },
          { label: "Reset Description", onClick: handleBulkResetDescription, isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true },
          { label: "Change Category", onClick: handleOpenChangeCategory, disabledOnNoRows: true },
        ]}
        expandedRow={expandedRow}
        extraRow={extraRow}
        showDateFilter={false}
        additionalBody={additionalBody}
      />
      {/* <div className="flex gap-x-2 items-center mt-4">
        <label>Page Size:</label>
        <input className="w-20" type="number" value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value))} />
      </div> */}
    </>
  );
}
